import React from 'react';
import { Row, Col } from 'antd';
import {
  type BrevoSyncEjecucion,
  type BrevoSyncRitmoPunto,
  transcurridoSegundos,
  estimacionRestanteSegundos,
  formatDuracion,
} from '../../api/brevoSyncApi';
import RitmoSparkline from './RitmoSparkline';
import { FctKpi, fmtNumero } from '../../fct/react';

interface StatCardsProps {
  ejecucion?: BrevoSyncEjecucion | null;
  ultimaFinalizada?: BrevoSyncEjecucion | null;
  ritmo?: BrevoSyncRitmoPunto[];
}

/** Indicadores de la sincronización como `FctKpi` (R-15): etiqueta, cifra en Mono y, en el ritmo, la sparkline como nota. */
const StatCards: React.FC<StatCardsProps> = ({ ejecucion, ultimaFinalizada, ritmo }) => {
  const isRunning = !!ejecucion;
  const ref = ejecucion || ultimaFinalizada;

  if (!ref) return null;

  // Transcurrido
  let transcurrido: string;
  if (isRunning) {
    transcurrido = formatDuracion(transcurridoSegundos(ejecucion!));
  } else if (ref.fin) {
    transcurrido = formatDuracion(Math.floor((new Date(ref.fin).getTime() - new Date(ref.inicio).getTime()) / 1000));
  } else {
    transcurrido = '—';
  }

  // Estimación
  let estimacion: string;
  if (isRunning) {
    const restante = estimacionRestanteSegundos(ejecucion!);
    estimacion = restante != null ? `~${formatDuracion(restante)}` : 'Calculando…';
  } else {
    estimacion = 'Finalizada';
  }

  // Ritmo (cifra y unidad por separado: la unidad va en pequeño junto a la cifra)
  let ritmoValor: string;
  let ritmoUnidad: string | undefined;
  if (isRunning) {
    const elapsed = transcurridoSegundos(ejecucion!);
    const total = ejecucion!.faseAExitos + ejecucion!.faseBExitos;
    ritmoValor = elapsed > 0 ? fmtNumero(Math.round(total / elapsed * 60)) : '—';
    ritmoUnidad = elapsed > 0 ? 'c/min' : undefined;
  } else {
    const dur = ref.fin ? Math.floor((new Date(ref.fin).getTime() - new Date(ref.inicio).getTime()) / 1000) : 0;
    const total = ref.faseAExitos + ref.faseBExitos;
    ritmoValor = dur > 0 ? fmtNumero(Math.round(total / dur * 60)) : '—';
    ritmoUnidad = dur > 0 ? 'c/min medio' : undefined;
  }

  // Duplicados
  const duplicados = ref.duplicadosFusionados;

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={12} lg={6}>
        <FctKpi etiqueta="Transcurrido" valor={transcurrido} />
      </Col>
      <Col xs={12} lg={6}>
        <FctKpi etiqueta="Estimación" valor={estimacion} />
      </Col>
      <Col xs={12} lg={6}>
        <FctKpi
          etiqueta="Ritmo"
          valor={ritmoValor}
          unidad={ritmoUnidad}
          nota={ritmo && ritmo.length >= 2 ? <RitmoSparkline puntos={ritmo} width={200} height={40} /> : undefined}
        />
      </Col>
      <Col xs={12} lg={6}>
        <FctKpi etiqueta="Duplicados" valor={fmtNumero(duplicados)} />
      </Col>
    </Row>
  );
};

export default StatCards;
